import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// ── Administration ▸ Plaques ────────────────────────────────────────────────
// Une plaque (ANT = Antilles, SELL = Sellens) est un PÉRIMÈTRE régional : elle
// dit qui voit quoi. Elle ne porte aucun réglage métier.
// Le rattachement d'une concession à sa plaque se fait sur la FICHE CONCESSION
// (Administration ▸ Concessions) — ici, la liste des concessions est en
// lecture seule : deux endroits pour décider d'un même fait finiraient par se
// contredire.

const EMPTY_FORM = { id: null, code: '', nom: '', actif: true };

export default function Plaques() {
  const [plaques, setPlaques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/plaques');
      setPlaques(res.data.plaques || []);
    } catch (err) {
      console.error('Erreur chargement plaques:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (p) => { setForm({ id: p.id, code: p.code, nom: p.nom, actif: p.actif }); setShowModal(true); };

  const save = async () => {
    if (!form.nom.trim() || (!form.id && !form.code.trim())) {
      alert('Code et nom obligatoires');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/plaques/${form.id}`, { nom: form.nom.trim(), actif: form.actif });
      } else {
        await api.post('/plaques', { code: form.code.trim().toUpperCase(), nom: form.nom.trim() });
      }
      setShowModal(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 13, maxWidth: 640 }}>
          Une plaque est un <strong>périmètre</strong> : un téléopérateur voit toutes les concessions de sa plaque et rien d'autre.
          Le rattachement d'une concession se règle sur sa <strong>fiche</strong> (onglet Concessions), pas ici.
        </div>
        <button className="btn-primary" onClick={openCreate}>+ Nouvelle plaque</button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Chargement…</p>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Nom</th>
                <th>Concessions</th>
                <th>Comptes rattachés directement</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plaques.map(p => (
                <tr key={p.id}>
                  <td className="font-bold">{p.code}</td>
                  <td>{p.nom}</td>
                  <td>
                    {(p.concessions || []).length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {p.concessions.map(c => (
                          <span key={c.id} className="filter-chip" title={c.nom} style={{ opacity: c.actif ? 1 : 0.5 }}>
                            {c.code}
                          </span>
                        ))}
                      </div>
                    ) : <span style={{ color: 'var(--text-muted)' }}>— aucune —</span>}
                  </td>
                  <td title="Comptes sans concession (téléopérateurs) rattachés à cette plaque">
                    {p.nb_comptes_directs}
                  </td>
                  <td>
                    <span className={`badge ${p.actif ? '' : 'inactive'}`}>{p.actif ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => openEdit(p)} className="btn-icon" title="Renommer / activer">✏️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {plaques.length === 0 && (
                <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>Aucune plaque.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{form.id ? 'Modifier la plaque' : 'Nouvelle plaque'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    value={form.code}
                    disabled={!!form.id}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="ANT, SELL…"
                    maxLength={10}
                  />
                  <small style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    2 à 10 lettres ou chiffres. Ne change plus après création : il sert d'identifiant dans les scripts d'import.
                  </small>
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Antilles, Sellens…" />
                </div>
                {form.id && (
                  <div className="form-group full">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
                      Plaque active
                    </label>
                    <small style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      Une plaque inactive ne peut plus recevoir de nouvelle concession. Les concessions déjà rattachées ne bougent pas.
                    </small>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
